import {useEffect, useState} from "react";
import {faImage, faPaperclip, faTrashCan} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Button as ButtonAntd, DatePicker, Input, List, Radio, Upload} from "antd";
import classNames from "classnames/bind";
import dayjs, {Dayjs} from "dayjs";
import Swal from "sweetalert2";

import Button from "~/components/button";
import {MergeGridItem} from "~/components/merge/merge-grid-item";
import {SideModal} from "~/components/modal/side-modal";
import {PageTitle} from "~/components/page-title";
import {IncidentBoardListType} from "~/data/board/incident/use-incident.interface";
import {useIncidentBoardDetail} from "~/data/board/incident/use-incident-board";
import {useUser} from "~/data/user";

import styles from "./incident-board.modal.module.scss";
import stylesGrid from "~/components/merge/merge-grid-item/merge-grid-item.module.scss";

const cx = classNames.bind(styles);
const gridCx = classNames.bind(stylesGrid);

const IncidentBoardModal = ({
    close,
    refresh,
    data,
}: {
    close: () => void;
    refresh: () => void;
    data: IncidentBoardListType | null;
}) => {
    const [inputs, setInputs] = useState<{
        title: string;
        dateRange: [Dayjs, Dayjs] | null;
        useYn: "Y" | "N";
        files: File[];
    }>({
        title: "",
        dateRange: null,
        useYn: "Y",
        files: [],
    });
    const [fileNameList, setFileNameList] = useState<{name: string; id: number}[]>([]);
    const [removalFileIds, setRemovalFileIds] = useState<number[]>([]);

    const {user} = useUser();

    const {detail, register, update, deleteIncident} = useIncidentBoardDetail({
        id: data?.id || undefined,
        title: inputs.title,
        startDate: inputs.dateRange ? inputs.dateRange[0].format("YYYY-MM-DD") : null,
        endDate: inputs.dateRange ? inputs.dateRange[1].format("YYYY-MM-DD") : null,
        useYn: inputs.useYn,
        files: inputs.files,
        removalFileIds: removalFileIds,
    });

    const handleRemoveFile = (fileName: string) => {
        setInputs((p) => ({...p, files: [...p.files.filter((item) => item.name !== fileName)]}));
        setFileNameList((p) => [...p.filter((item) => item.name !== fileName)]);
    };

    const handleConfirm = () => {
        if (inputs.dateRange) {
            Swal.fire({
                text: `${detail ? "수정" : "등록"}하시겠습니까?`,
                showCancelButton: true,
                confirmButtonText: detail ? "수정하기" : "등록하기",
                cancelButtonText: "취소하기",
            })
                .then((info) => {
                    if (info.isConfirmed) {
                        if (detail) update();
                        else register();
                    }
                })
                .finally(() => {
                    setTimeout(refresh, 500);
                    close();
                });
        } else {
            Swal.fire({
                text: "기간을 설정해주세요",
                confirmButtonText: "확인",
            });
        }
    };
    const handleDelete = () => {
        if (detail)
            Swal.fire({
                text: `삭제하시겠습니까?`,
                showCancelButton: true,
                confirmButtonText: "삭제하기",
                cancelButtonText: "취소하기",
            })
                .then((info) => {
                    if (info.isConfirmed) {
                        deleteIncident(detail.board.id);
                    }
                })
                .finally(() => {
                    setTimeout(refresh, 500);
                    close();
                });
    };

    useEffect(() => {
        if (detail) {
            setInputs({
                title: detail.board.title || "",
                dateRange: detail.board.startDate ? [dayjs(detail.board.startDate), dayjs(detail.board.endDate)] : null,
                useYn: detail.board.useYn,
                files: [],
            });

            setFileNameList(
                detail.files.map((item) => ({
                    id: item.id,
                    name: item.fileOriginName,
                })),
            );
        }
    }, [detail]);

    return (
        <SideModal
            onClose={close}
            nonePrevent
            confirmLabel={detail ? "수정하기" : "등록하기"}
            onConfirm={handleConfirm}
            extraButton={
                user?.division === "MEMBER" && detail ? (
                    <Button label={"삭제하기"} onClick={handleDelete} key={"dlt"} />
                ) : undefined
            }
        >
            <>
                <PageTitle>사고사례 등록</PageTitle>
                <div className={cx("form-container")}>
                    <div className={cx("content-container")}>
                        <MergeGridItem name={"제목"} span>
                            <Input
                                name={"title"}
                                placeholder={"제목을 입력하세요"}
                                value={inputs.title}
                                onChange={(e) => setInputs((p) => ({...p, title: e.target.value}))}
                            />
                        </MergeGridItem>
                        <MergeGridItem name={"팝업 여부"}>
                            <Radio.Group
                                value={inputs.useYn}
                                onChange={(e) => setInputs((p) => ({...p, useYn: e.target.value}))}
                            >
                                <Radio value="Y">사용</Radio>
                                <Radio value="N">미사용</Radio>
                            </Radio.Group>
                        </MergeGridItem>
                        <MergeGridItem name={"기간"}>
                            <DatePicker.RangePicker
                                className={cx("date-picker")}
                                value={inputs.dateRange}
                                //eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                //@ts-ignore
                                onChange={(e) => setInputs((p) => ({...p, dateRange: e}))}
                            />
                        </MergeGridItem>
                        <MergeGridItem name={"파일 업로드"} span extraClassName={"upload-container"}>
                            <div className={gridCx("file-container")}>
                                <Upload.Dragger
                                    className={gridCx("upload-dragger")}
                                    name={"file"}
                                    accept={".jpg,.jpeg,.png,.gif"}
                                    multiple={true}
                                    maxCount={5}
                                    showUploadList={false}
                                    beforeUpload={(e) => {
                                        setInputs((prev) => {
                                            const newFiles = [...prev.files];
                                            newFiles.push(e);
                                            return {
                                                ...prev,
                                                files: newFiles,
                                            };
                                        });
                                        setFileNameList((p) => [...p, {name: e.name, id: 0}]);
                                        return false;
                                    }}
                                >
                                    {fileNameList.length ? (
                                        <List
                                            itemLayout="horizontal"
                                            dataSource={fileNameList}
                                            className={gridCx("file-list")}
                                            renderItem={(file, index) => (
                                                <List.Item
                                                    className={gridCx("file-list-item")}
                                                    key={index}
                                                    actions={[
                                                        // <ButtonAntd
                                                        //     type="link"
                                                        //     key={index}
                                                        //     icon={<FontAwesomeIcon icon={faDownload} />}
                                                        //     onClick={(e) => {
                                                        //         e.stopPropagation();
                                                        //         // fileDownload(file.id);
                                                        //     }}
                                                        // />,
                                                        <ButtonAntd
                                                            type="link"
                                                            danger
                                                            key={index}
                                                            icon={<FontAwesomeIcon icon={faTrashCan} />}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRemoveFile(file.name);
                                                                setRemovalFileIds((p) => [...p, file.id]);
                                                            }}
                                                            // disabled={!isOwner && noticeId !== null}
                                                        />,
                                                    ]}
                                                >
                                                    <List.Item.Meta
                                                        avatar={<FontAwesomeIcon icon={faPaperclip} />}
                                                        title={file.name}
                                                        className={gridCx("file-list-item-meta")}
                                                        style={{textAlign: "left"}}
                                                    />
                                                </List.Item>
                                            )}
                                        />
                                    ) : (
                                        <p>드래그하여 파일을 업로드 하거나 파일을 선택하세요</p>
                                    )}
                                </Upload.Dragger>
                            </div>
                        </MergeGridItem>
                    </div>
                </div>
                {detail && detail?.files.length ? (
                    <div className={cx("image-container")}>
                        <p>이미지 미리보기</p>
                        {detail.files.map((item, index) => (
                            <div className={cx("images")} key={index}>
                                <span>
                                    <FontAwesomeIcon icon={faImage} className={cx("icon")} />
                                    {item.fileOriginName}
                                </span>
                                <img alt={"image"} src={"data:image/png;base64," + item.base64} />
                            </div>
                        ))}
                    </div>
                ) : undefined}
            </>
        </SideModal>
    );
};

export {IncidentBoardModal};
